import { ThirdPartyRefundProvider } from "@redotech/cockroachdb-codegen/congodb/third-party-refund-provider";
import { Currency } from "@redotech/money/currencies";
import { getCurrencyFormatter } from "@redotech/money/util";
import type { RefundDestinationForm } from "@redotech/redo-customer-portal-shared/hooks/useRefundDestination/use-refund-destination-form";
import { useTranslation } from "@redotech/redo-i18n";
import BankIcon from "@redotech/redo-icons/bank.svg";
import PaypalIcon from "@redotech/redo-icons/paypal-color.svg";
import VenmoIcon from "@redotech/redo-icons/venmo-color.svg";
import {
  RefundDestinationProvider,
  RefundRecipientField,
  thirdPartyRefundProviderName,
} from "@redotech/redo-return-core-common/third-party-refunds/recipient";
import {
  RedoInputState,
  sizeToDescriptorTextProps,
} from "@redotech/redo-web/arbiter-components/input/base-redo-text-input";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode } from "react";
import { PortalSelectionCard } from "../submission/components/portal-selection-card";

export const ORIGINAL_PAYMENT = "original" as const;

const INPUT_SIZE = "sm" as const;

export interface RefundDestinationOptionsProps {
  form: RefundDestinationForm;
  /** Refund total in `currency`; drives the per-option description. */
  refundAmount: number;
  currency: Currency;
  /** Whether refunding to the original payment method is still on the table. */
  allowOriginalPayment: boolean;
  /** Shown next to the recipient field while a save is in flight. */
  saving?: boolean;
  errorMessage?: string;
  onRecipientBlur?: () => void;
}

/**
 * The rail picker itself, with the recipient field nested under the selected
 * option. Shared by the submission review screen and the post-submission
 * editor so the two can't drift apart visually or behaviourally.
 */
export const RefundDestinationOptions = memo(function RefundDestinationOptions({
  form,
  refundAmount,
  currency,
  allowOriginalPayment,
  saving = false,
  errorMessage,
  onRecipientBlur,
}: RefundDestinationOptionsProps) {
  const { t, i18n } = useTranslation();
  const {
    providers,
    selectedProvider,
    selectProvider,
    recipientField,
    recipientValue,
    setRecipientValue,
    markTouched,
    orderMismatchWarning,
  } = form;

  const amount = getCurrencyFormatter(
    currency,
    undefined,
    i18n.language,
  ).format(refundAmount);

  const isPhone = recipientField === RefundRecipientField.PHONE;
  const hasError = !!errorMessage;
  const inputState: RedoInputState = hasError ? "error" : "default";
  // Matches the size the input uses for its own label/error text.
  const descriptorTextSize = sizeToDescriptorTextProps[INPUT_SIZE];

  const recipientInput = (
    // Editing shouldn't register as a click on the card wrapping the field.
    <Flex
      dir="column"
      gap="xs"
      onClick={(event) => event.stopPropagation()}
      pt="lg"
      w="full"
    >
      <RedoTextInput
        ariaInvalid={hasError}
        autoSave={{ isSaving: saving }}
        dataTestId="refund-destination-recipient"
        errorText={errorMessage}
        fullWidth
        inputMode={isPhone ? "tel" : "email"}
        inputTypeHint={isPhone ? "tel" : "email"}
        label={isPhone ? t("Venmo mobile number") : t("PayPal email")}
        onBlur={onRecipientBlur ?? markTouched}
        placeholder={isPhone ? t("(555) 555-5555") : t("you@example.com")}
        setValue={setRecipientValue}
        size={INPUT_SIZE}
        state={inputState}
        value={recipientValue}
      />
      {!errorMessage && !!orderMismatchWarning && (
        <Text as="p" fontSize={descriptorTextSize} textColor="warning">
          {orderMismatchWarning}
        </Text>
      )}
    </Flex>
  );

  const providerCopy: Record<
    RefundDestinationProvider,
    { icon: ReactNode; label: string; description: string }
  > = {
    [ThirdPartyRefundProvider.Paypal]: {
      icon: <PaypalIcon height={32} width={32} />,
      label: thirdPartyRefundProviderName(ThirdPartyRefundProvider.Paypal),
      description: t("{{amount}} to your PayPal account", { amount }),
    },
    [ThirdPartyRefundProvider.Venmo]: {
      icon: <VenmoIcon height={32} width={32} />,
      label: thirdPartyRefundProviderName(ThirdPartyRefundProvider.Venmo),
      description: t("{{amount}} back to your Venmo account", { amount }),
    },
  };

  const options: {
    id: typeof ORIGINAL_PAYMENT | RefundDestinationProvider;
    icon: ReactNode;
    isFramedIcon: boolean;
    label: string;
    description: string;
  }[] = [
    ...(allowOriginalPayment
      ? [
          {
            id: ORIGINAL_PAYMENT,
            icon: <BankIcon height={16} width={16} />,
            isFramedIcon: true,
            label: t("Original payment method"),
            description: t("{{amount}} back to your account", { amount }),
          },
        ]
      : []),
    ...providers.map((provider) => ({
      id: provider,
      isFramedIcon: false,
      ...providerCopy[provider],
    })),
  ];

  const selectedId = selectedProvider ?? ORIGINAL_PAYMENT;

  // Nothing can pay this refund: no wallet rail, and the original payment
  // method can't settle it either. Offering an empty picker would be worse
  // than saying nothing.
  if (options.length === 0) {
    return null;
  }

  return (
    <Flex data-ui="refund-method-options" flexDirection="column" gap="2xl">
      {options.map((option) => {
        const selected = option.id === selectedId;
        return (
          <PortalSelectionCard
            childrenFullWidth
            icon={option.icon}
            inputType="radio"
            isFramedIcon={option.isFramedIcon}
            key={option.id}
            onClick={() => {
              // Re-selecting the current rail would discard a pending entry.
              if (selected) {
                return;
              }
              selectProvider(option.id === ORIGINAL_PAYMENT ? null : option.id);
            }}
            selected={selected}
            title={option.label}
            uiSelector={`refund-method-${option.id}`}
          >
            <Flex dir="column" gap="none" w="full">
              <Text as="p" fontSize="sm" textColor="tertiary">
                {option.description}
              </Text>
              {selected && option.id !== ORIGINAL_PAYMENT && recipientInput}
            </Flex>
          </PortalSelectionCard>
        );
      })}
    </Flex>
  );
});
